'use client'

import React, { useEffect, useRef, useState } from 'react'
import { useAnimation } from '@/providers/Animation'

/**
 * FlameSmokeBackground
 * --------------------
 * Site-wide interactive background: a field of monospace glyphs that behaves
 * like slow drifting smoke over a dark page, plus a click-driven particle layer.
 *
 * Smoke — two fields advected along a shared, time-evolving flow field:
 *   • density (`den`)     — how much smoke is in a cell (→ glyph + opacity)
 *   • temperature (`tmp`) — how hot/fresh it is (→ warm ember → grey colour)
 * The whole page always has a gentle ambient haze drifting upward; the cursor
 * is a warm, dense emitter feeding the existing flow.
 *
 * Click — charge + disperse, layered on top of the smoke:
 *   • mousedown → a warm "charge" gathers at the cursor: crisp ember-gold sparks
 *     spiral inward and drift up (harmonising with the rising flame), a thin
 *     gold ring tightens, and the smoke emitter glows brighter. Hold longer to
 *     charge more (→ a bigger burst, up to a preset max radius).
 *   • mouseup → an energetic burst of iridescent "chameleon" particles (hue
 *     rotates over life) — glittery sparks + smoke puffs in venom colours. The
 *     warm charge erupts into the exotic reveal; burst radius scales with charge.
 *
 * Renders nothing when animations are disabled via the AnimationProvider toggle,
 * on prefers-reduced-motion, or before hydration. The <canvas> is
 * pointer-events:none and reads the pointer from window listeners, so it never
 * blocks clicks on the store beneath it.
 */

// Glyph ramp by density: thin smoke (low) → thick smoke (high). Index 0 = none.
const RAMP = ' .,:;-=+o*#%@'

// --- smoke tunables ---------------------------------------------------------
const CELL = 18 // px per glyph cell
const TIME_SCALE = 0.01 // flow evolution speed
const FX = 0.09 // flow spatial frequency (x)
const FY = 0.08 // flow spatial frequency (y)
const SWIRL = 1.1 // turbulence amplitude (radians)
const SWAY = 0.45 // horizontal drift (cells/frame)
const RISE = 0.28 // upward drift (cells/frame)
const DEN_DISSIPATE = 0.986 // how slowly smoke clears
const TMP_DISSIPATE = 0.95 // temperature cools faster than smoke clears
const AMBIENT = 0.05 // baseline smoke emitted along the bottom each frame
const CURSOR_DEN = 0.5 // smoke added at the cursor
const CURSOR_TMP = 1.0 // heat added at the cursor
const CURSOR_R = 3 // cursor emitter radius (cells)

// --- click / particle tunables ----------------------------------------------
const CHARGE_TIME = 75 // frames of holding to reach full charge (~1.25s)
const BURST_MIN = 22 // particles on a quick tap
const BURST_SCALE = 220 // extra particles at full charge
const BURST_MIN_RADIUS = 70 // px reach on a quick tap
const BURST_MAX_RADIUS_FRAC = 0.55 // full-charge reach = frac × min(viewport). Bump toward ~0.9 for ~full-screen.
const MAX_PARTICLES = 800 // hard cap
const DRAG = 0.93 // particle velocity damping per frame
// Warm ember "charge" palette (HSL degrees, gold→orange) — harmonises with the
// flame so the build-up feels like gathering the fire's energy.
const CHARGE_HUES = [26, 32, 38, 44]
// "Venomous glittery chameleon" burst hues: lime, green, emerald, teal, cyan,
// violet, magenta, hot-pink — each burst particle also rotates its hue over life.
const VENOM = [95, 120, 150, 168, 188, 275, 300, 322]
const SPARK_CHARS = ['*', '+', 'x', '.']
const PUFF_CHARS = ['o', 'O', '%', '#', '&', '@', '0']

interface Particle {
  x: number
  y: number
  vx: number
  vy: number
  life: number
  maxLife: number
  hue: number
  hueRate: number
  size: number
  kind: 'charge' | 'spark' | 'puff'
  glint: boolean
  char: string
  phase: number
}

export const FlameSmokeBackground: React.FC = () => {
  const { animationsEnabled } = useAnimation()
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [isClient, setIsClient] = useState(false)
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false)

  useEffect(() => {
    setIsClient(true)
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    setPrefersReducedMotion(mq.matches)
    const handler = (e: MediaQueryListEvent) => setPrefersReducedMotion(e.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])

  useEffect(() => {
    if (!isClient || !animationsEnabled || prefersReducedMotion) return
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const GRID_FONT = `${CELL}px ui-monospace, "Geist Mono", SFMono-Regular, Menlo, monospace`

    // --- smoke colour palette: cool grey → amber → bright cream (warm core) ---
    const PAL = 32
    const palette: string[] = []
    const lerp = (a: number, b: number, f: number) => Math.round(a + (b - a) * f)
    const COOL = [184, 190, 201]
    const MID = [255, 150, 72]
    const HOT = [255, 214, 150]
    for (let i = 0; i < PAL; i++) {
      const f = i / (PAL - 1)
      let r: number, g: number, b: number
      if (f < 0.5) {
        const k = f / 0.5
        r = lerp(COOL[0], MID[0], k)
        g = lerp(COOL[1], MID[1], k)
        b = lerp(COOL[2], MID[2], k)
      } else {
        const k = (f - 0.5) / 0.5
        r = lerp(MID[0], HOT[0], k)
        g = lerp(MID[1], HOT[1], k)
        b = lerp(MID[2], HOT[2], k)
      }
      palette.push(`rgb(${r},${g},${b})`)
    }

    // --- grid + fields -------------------------------------------------------
    let cols = 0
    let rows = 0
    let den = new Float32Array(0)
    let tmp = new Float32Array(0)
    let den2 = new Float32Array(0)
    let tmp2 = new Float32Array(0)

    const resize = () => {
      const cssW = window.innerWidth
      const cssH = window.innerHeight
      const dpr = Math.min(window.devicePixelRatio || 1, 2)
      canvas.width = Math.floor(cssW * dpr)
      canvas.height = Math.floor(cssH * dpr)
      canvas.style.width = `${cssW}px`
      canvas.style.height = `${cssH}px`
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      ctx.textBaseline = 'top'
      cols = Math.ceil(cssW / CELL) + 1
      rows = Math.ceil(cssH / CELL) + 1
      den = new Float32Array(cols * rows)
      tmp = new Float32Array(cols * rows)
      den2 = new Float32Array(cols * rows)
      tmp2 = new Float32Array(cols * rows)
      for (let r = 0; r < rows; r++) {
        const base = (r / rows) * 0.18
        for (let c = 0; c < cols; c++) den[r * cols + c] = base * Math.random()
      }
    }
    resize()

    const sample = (buf: Float32Array, x: number, y: number) => {
      if (x < 0) x = 0
      else if (x > cols - 1) x = cols - 1
      if (y < 0) y = 0
      else if (y > rows - 1) y = rows - 1
      const x0 = x | 0
      const y0 = y | 0
      const x1 = x0 + 1 < cols ? x0 + 1 : x0
      const y1 = y0 + 1 < rows ? y0 + 1 : y0
      const fx = x - x0
      const fy = y - y0
      const r0 = y0 * cols
      const r1 = y1 * cols
      const top = buf[r0 + x0] + (buf[r0 + x1] - buf[r0 + x0]) * fx
      const bot = buf[r1 + x0] + (buf[r1 + x1] - buf[r1 + x0]) * fx
      return top + (bot - top) * fy
    }

    // --- pointer + charge state ----------------------------------------------
    let mouseX = -1
    let mouseY = -1
    let inside = false
    let charging = false
    let chargeStart = 0
    let chargeLevel = 0
    const bursts: { x: number; y: number; level: number }[] = []
    const particles: Particle[] = []

    const pick = (arr: string[]) => arr[(Math.random() * arr.length) | 0]

    // Warm ember sparks spiralling inward + up while charging (crisp, saturated).
    const spawnConverge = (cx: number, cy: number, level: number) => {
      const ang = Math.random() * Math.PI * 2
      const rad = 28 + Math.random() * 46
      const inward = (0.55 + level) * (rad / 24)
      const tangent = 0.3 + Math.random() * 0.5 // swirl, echoing the rising smoke
      const life = 12 + Math.random() * 9
      particles.push({
        x: cx + Math.cos(ang) * rad,
        y: cy + Math.sin(ang) * rad,
        vx: -Math.cos(ang) * inward - Math.sin(ang) * tangent,
        vy: -Math.sin(ang) * inward + Math.cos(ang) * tangent - 0.3, // slight upward bias
        life,
        maxLife: life,
        hue: CHARGE_HUES[(Math.random() * CHARGE_HUES.length) | 0] + (Math.random() * 8 - 4),
        hueRate: (Math.random() * 2 - 1) * 0.4, // stays warm → crisp, consistent colour
        size: 8 + Math.random() * 4, // small + sharp
        kind: 'charge',
        glint: Math.random() < 0.28,
        char: pick(SPARK_CHARS),
        phase: Math.random() * 6.28,
      })
    }

    // The release burst: iridescent chameleon sparks + puffs. Reach (radius)
    // scales with charge, capped at a preset max; a grid shove leaves a puff.
    const spawnBurst = (
      cx: number,
      cy: number,
      level: number,
      dBuf: Float32Array,
      tBuf: Float32Array,
    ) => {
      const maxR = Math.min(window.innerWidth, window.innerHeight) * BURST_MAX_RADIUS_FRAC
      const radius = BURST_MIN_RADIUS + (maxR - BURST_MIN_RADIUS) * level
      const count = Math.round(BURST_MIN + level * BURST_SCALE)
      for (let i = 0; i < count; i++) {
        const ang = Math.random() * Math.PI * 2
        const reach = radius * (0.45 + Math.random() * 0.75)
        const v0 = reach * (1 - DRAG) // initial speed that decays to ~reach px
        const spark = Math.random() < 0.5
        const life = (spark ? 42 : 55) * (0.8 + level * 0.6) + Math.random() * (spark ? 26 : 44)
        particles.push({
          x: cx,
          y: cy,
          vx: Math.cos(ang) * v0,
          vy: Math.sin(ang) * v0,
          life,
          maxLife: life,
          hue: VENOM[(Math.random() * VENOM.length) | 0] + (Math.random() * 40 - 20),
          hueRate: (Math.random() * 2 - 1) * 2.4,
          size: spark ? 10 + Math.random() * 7 : 16 + Math.random() * 14,
          kind: spark ? 'spark' : 'puff',
          glint: spark && Math.random() < 0.18,
          char: spark ? pick(SPARK_CHARS) : pick(PUFF_CHARS),
          phase: Math.random() * 6.28,
        })
      }
      // shove the smoke field so a lingering puff drifts off the burst
      const mc = Math.floor(cx / CELL)
      const mr = Math.floor(cy / CELL)
      const R = 3 + Math.round(level * 3)
      for (let dr = -R; dr <= R; dr++) {
        for (let dc = -R; dc <= R; dc++) {
          const rr = mr + dr
          const cc = mc + dc
          if (rr < 0 || rr >= rows || cc < 0 || cc >= cols) continue
          const dist = Math.sqrt(dr * dr + dc * dc)
          if (dist > R) continue
          const f = 1 - dist / R
          const idx = rr * cols + cc
          dBuf[idx] = Math.min(1, dBuf[idx] + f * 0.5 * (0.6 + level))
          tBuf[idx] = Math.min(1, tBuf[idx] + f * 0.4)
        }
      }
      if (particles.length > MAX_PARTICLES) particles.splice(0, particles.length - MAX_PARTICLES)
    }

    // --- simulation + render -------------------------------------------------
    let raf = 0
    let frame = 0

    const step = () => {
      const t = frame * TIME_SCALE

      // 1. advect both fields backward along the flow field.
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          const angle = (Math.sin(c * FX + t) + Math.cos(r * FY - t * 0.8)) * SWIRL
          const vx = Math.cos(angle) * SWAY
          const vy = Math.sin(angle) * SWAY - RISE
          const i = r * cols + c
          den2[i] = sample(den, c - vx, r - vy) * DEN_DISSIPATE
          tmp2[i] = sample(tmp, c - vx, r - vy) * TMP_DISSIPATE
        }
      }

      // 2. ambient emission — wispy band along the bottom + scattered wisps.
      const rb = rows - 1
      for (let c = 0; c < cols; c++) {
        const wob = 0.5 + 0.5 * Math.sin(c * 0.25 + t * 1.7)
        const amt = AMBIENT * (0.35 + 0.65 * wob)
        const i0 = rb * cols + c
        const i1 = (rb - 1) * cols + c
        den2[i0] = Math.min(1, den2[i0] + amt)
        den2[i1] = Math.min(1, den2[i1] + amt * 0.7)
      }
      const wisps = Math.max(1, (cols / 40) | 0)
      for (let k = 0; k < wisps; k++) {
        const cc = (Math.random() * cols) | 0
        const rr = ((0.35 + Math.random() * 0.6) * rows) | 0
        const i = rr * cols + cc
        den2[i] = Math.min(1, den2[i] + AMBIENT * 0.7)
      }

      // 3. drain any release bursts (particles + grid shove).
      for (const b of bursts) spawnBurst(b.x, b.y, b.level, den2, tmp2)
      bursts.length = 0

      // 4. charge visuals while the button is held.
      if (charging) {
        chargeLevel = Math.min(1, (frame - chargeStart) / CHARGE_TIME)
        const rate = 1 + Math.round(chargeLevel * 3)
        for (let k = 0; k < rate; k++) spawnConverge(mouseX, mouseY, chargeLevel)
      } else {
        chargeLevel = 0
      }

      // 5. cursor emitter (brighter while charging).
      if (inside && mouseX >= 0) {
        const boost = charging ? 1 + chargeLevel * 1.5 : 1
        const mc = Math.floor(mouseX / CELL)
        const mr = Math.floor(mouseY / CELL)
        for (let dr = -CURSOR_R; dr <= CURSOR_R; dr++) {
          for (let dc = -CURSOR_R; dc <= CURSOR_R; dc++) {
            const rr = mr + dr
            const cc = mc + dc
            if (rr < 0 || rr >= rows || cc < 0 || cc >= cols) continue
            const dist = Math.sqrt(dr * dr + dc * dc)
            if (dist > CURSOR_R) continue
            const f = 1 - dist / CURSOR_R
            const i = rr * cols + cc
            den2[i] = Math.min(1, den2[i] + f * CURSOR_DEN * boost * (0.8 + Math.random() * 0.2))
            tmp2[i] = Math.min(1, tmp2[i] + f * CURSOR_TMP * boost)
          }
        }
      }

      // 6. swap buffers.
      let s = den
      den = den2
      den2 = s
      s = tmp
      tmp = tmp2
      tmp2 = s

      // 7. render smoke grid.
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      ctx.font = GRID_FONT
      const last = RAMP.length - 1
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          const i = r * cols + c
          const d = den[i]
          if (d < 0.05) continue
          const idx = d >= 1 ? last : (d * last) | 0
          const ch = RAMP[idx]
          if (ch === ' ') continue
          let tv = tmp[i]
          if (tv > 1) tv = 1
          ctx.fillStyle = palette[tv >= 1 ? PAL - 1 : (tv * PAL) | 0]
          let alpha = 0.12 + d * 0.68 + tv * 0.2
          if (alpha > 0.92) alpha = 0.92
          ctx.globalAlpha = alpha
          ctx.fillText(ch, c * CELL, r * CELL)
        }
      }

      // 8. update + render the particle layer (on top of the smoke).
      for (let p = particles.length - 1; p >= 0; p--) {
        const q = particles[p]
        q.x += q.vx
        q.y += q.vy
        q.vx *= DRAG
        q.vy *= DRAG
        if (q.kind === 'puff') {
          q.vy -= 0.03 // puffs rise like smoke
          q.size *= 1.012 // and expand
        }
        q.hue += q.hueRate
        q.life -= 1
        if (q.life <= 0) {
          particles.splice(p, 1)
          continue
        }
        let a = q.life / q.maxLife
        let fill: string
        const hue = (((q.hue % 360) + 360) % 360) | 0
        if (q.kind === 'charge') {
          a *= 0.82 + 0.18 * Math.sin(frame * 0.5 + q.phase) // crisp, high floor
          fill = q.glint ? '#fff2d6' : `hsl(${hue}, 100%, 66%)` // saturated ember-gold
        } else if (q.kind === 'spark') {
          a *= 0.55 + 0.45 * Math.sin(frame * 0.6 + q.phase) // glittery twinkle
          fill = q.glint ? '#ffffff' : `hsl(${hue}, 95%, 68%)`
        } else {
          a *= 0.72 // puffs softer
          fill = `hsl(${hue}, 95%, 56%)`
        }
        if (a <= 0) continue
        if (a > 1) a = 1
        ctx.globalAlpha = a
        ctx.fillStyle = fill
        ctx.font = `${q.size | 0}px ui-monospace, "Geist Mono", monospace`
        ctx.fillText(q.char, q.x, q.y)
      }

      // 9. charge ring — a crisp warm-gold circle that tightens + brightens
      //    toward white-gold as it fills, complementing the flame.
      if (charging) {
        const pulse = 0.5 + 0.5 * Math.sin(frame * 0.3)
        const radius = 10 + (1 - chargeLevel) * 64
        ctx.globalAlpha = 0.3 + 0.55 * chargeLevel
        ctx.strokeStyle = `hsl(${40 - chargeLevel * 6}, 100%, ${58 + chargeLevel * 30}%)`
        ctx.lineWidth = 1.5 + chargeLevel * 1.2 * pulse
        ctx.beginPath()
        ctx.arc(mouseX, mouseY, radius, 0, Math.PI * 2)
        ctx.stroke()
      }

      ctx.globalAlpha = 1
      frame++
      raf = requestAnimationFrame(step)
    }

    // --- listeners -----------------------------------------------------------
    const onMove = (e: MouseEvent) => {
      mouseX = e.clientX
      mouseY = e.clientY
      inside = true
    }
    const onDown = (e: MouseEvent) => {
      mouseX = e.clientX
      mouseY = e.clientY
      inside = true
      charging = true
      chargeStart = frame
    }
    const onUp = (e: MouseEvent) => {
      if (!charging) return
      const level = Math.min(1, (frame - chargeStart) / CHARGE_TIME)
      bursts.push({ x: e.clientX, y: e.clientY, level })
      charging = false
    }
    const onLeave = () => {
      inside = false
      charging = false
    }
    const onResize = () => resize()

    window.addEventListener('mousemove', onMove, { passive: true })
    window.addEventListener('mousedown', onDown, { passive: true })
    window.addEventListener('mouseup', onUp, { passive: true })
    document.addEventListener('mouseleave', onLeave)
    window.addEventListener('blur', onLeave)
    window.addEventListener('resize', onResize)

    raf = requestAnimationFrame(step)

    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mousedown', onDown)
      window.removeEventListener('mouseup', onUp)
      document.removeEventListener('mouseleave', onLeave)
      window.removeEventListener('blur', onLeave)
      window.removeEventListener('resize', onResize)
    }
  }, [isClient, animationsEnabled, prefersReducedMotion])

  if (!isClient || !animationsEnabled || prefersReducedMotion) return null

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className="fixed inset-0 pointer-events-none"
      style={{ zIndex: 1 }}
    />
  )
}

export default FlameSmokeBackground
